import { useCallback } from 'react';

import { Toaster } from '@picsart/design-system/components/Toaster';
import {
  Snackbar,
  type SnackbarSkins,
} from '@picsart/design-system/components/Snackbar';
import {
  Notification,
  type NotificationStatuses,
} from '@picsart/design-system/components/Notification';
import { Button } from '@picsart/design-system/components/Button';
import { useTranslator } from '@picsart/rc/services/localization';
import { useAppStore, type IAppStore } from '@/store';
import useLocalNotification from '@/hooks/useLocalNotification';
import { ToastType } from '@/store/toast/types';

const selector = (store: IAppStore) => ({
  toast: store.toast,
  hideToast: store.hideToast,
});

const toastTypeToSnackbarSkin = (type: ToastType): SnackbarSkins => {
  switch (type) {
    case ToastType.Error:
      return 'error';
    case ToastType.Info:
      return 'information';
    case ToastType.Success:
    default:
      return 'success';
  }
};

const toastTypeToNotificationStatus = (
  type: ToastType
): NotificationStatuses => {
  switch (type) {
    case ToastType.Error:
      return 'error';
    case ToastType.Info:
      return 'information';
    case ToastType.Success:
    default:
      return 'success';
  }
};

/**
 * Single toast surface. A local notification (with an action button —
 * e.g. "Your creation is ready! [View]") takes precedence over the global
 * info/error toast. Local notifications use DS `Notification` (banner with
 * a textual action button); plain toasts use DS `Snackbar`. Both are hosted
 * in a DS `Toaster` positioned at the bottom-center.
 */
const ToastSurface = () => {
  const translate = useTranslator();
  const { toast: notification, hideToast: hideNotification } =
    useLocalNotification();
  const { toast: globalToast, hideToast: hideGlobalToast } =
    useAppStore(selector);

  const handleNotificationAction = useCallback(() => {
    notification?.action?.onClick();
    hideNotification();
  }, [notification, hideNotification]);

  const isOpen = Boolean(notification || globalToast);

  // Use the local notification's onClose if a notification is shown,
  // otherwise the global toast's hideToast.
  const handleClose = notification ? hideNotification : hideGlobalToast;

  return (
    <Toaster
      dataTestId="toast-surface"
      isOpen={isOpen}
      position="bottomCenter"
      onClose={handleClose}
      isWithoutTiming
    >
      {notification ? (
        <Notification
          dataTestId="local-notification"
          status={toastTypeToNotificationStatus(notification.type)}
          size="small"
          description={translate(notification.message)}
          onClose={hideNotification}
          actions={
            notification.action
              ? [
                  <Button
                    key="action"
                    dataTestId="notification-action"
                    skin="primary"
                    variant="text"
                    size="small"
                    onClick={handleNotificationAction}
                  >
                    {translate(notification.action.label)}
                  </Button>,
                ]
              : undefined
          }
        />
      ) : globalToast ? (
        <Snackbar
          dataTestId="toast"
          skin={toastTypeToSnackbarSkin(globalToast.type)}
          title={translate(globalToast.message)}
          handleClose={hideGlobalToast}
        />
      ) : null}
    </Toaster>
  );
};

export default ToastSurface;
